import React from "react";
import {Box, Typography} from "@material-ui/core/";
import MenuBookOutlinedIcon from "@material-ui/icons/MenuBookOutlined";
import clsx from "clsx";
import {useTranslate} from "@tolgee/react";
import {useCurrentPublicShow} from "hooks";
import {shallow} from "zustand/shallow";
import useDateFnsLocale from "hooks/useDateFnsLocale";
import useShowCustomFields from "hooks/useShowCustomFields";
import useCurrentExhibitorPairId from "hooks/useCurrentExhibitorPairId";
import {useCurrentExhibitor} from "store/current-exhibitor/current-exhibitor.store";
import {useLocalMapContext} from "view/context/LocalMapContext";
import {ICategory, IGlossary, ITerm} from "@evennt-tech/api-v2-core";
import GlossaryTermDescription from "view/components/GlossaryTerm/Description";
import getTextWithVariables from "tools/task/getTextWithVariables";
import {isLinkedTerm} from "api/showInfoLibrary/types";
import GlossaryTermActions, {Actions} from "./GlossaryTermActions";
import useStyles from "./styles";

export type GlossaryTermProps = {
  term: ITerm;
  glossary: IGlossary;
  actions?: Actions;
  terms: ITerm[];
  categories: ICategory[];
  isPrintView?: boolean;
};

const GlossaryTerm: React.FC<GlossaryTermProps> = (args) => {
  const {term, actions, terms, categories, isPrintView} = args;
  const classes = useStyles({interactive: !!actions});
  const [show] = useCurrentPublicShow();
  const {organiser} = useLocalMapContext();
  const {t} = useTranslate();
  const locale = useDateFnsLocale();

  /*
   * Custom-field variables resolve as they do for a task: keyed by the field's
   * English title, valued from the reader's own stand. The pair is only read
   * here, never fetched — the exhibitor pages that render a manual have it, and
   * an organiser has none, so their tokens fall back.
   */
  const [customFields] = useShowCustomFields({localised: true});
  const exhibitorPairId = useCurrentExhibitorPairId();
  const {pair} = useCurrentExhibitor(
    (state) => state.getExhibitorPair(show?.id ?? "", exhibitorPairId),
    shallow
  );
  const standToCustomFields = pair?.stand?.standToCustomFields;

  const isLinked = isLinkedTerm(term);
  /*
   * The badge and the tint are organiser-only: where content came from is no
   * concern of an exhibitor's. `actions` is the marker — `readonly` callers pass
   * none — and print keeps the card but drops the badge.
   */
  const showLinkedState = isLinked && !!actions;
  const showLinkedBadge = showLinkedState && !isPrintView;

  return (
    <div className={clsx(classes.root, {[classes.linked]: showLinkedState})}>
      {showLinkedBadge && (
        <Box className={classes.linkedBadge}>
          linked to Library
          <MenuBookOutlinedIcon className={classes.linkedBadgeIcon} />
        </Box>
      )}
      <Box className={classes.header}>
        <Box display="flex" position="relative">
          <Typography variant="h2" className={classes.title}>
            {term.title}
          </Typography>
        </Box>
        <GlossaryTermActions
          term={term}
          terms={terms}
          categories={categories}
          actions={actions}
          isPrintView={isPrintView}
        />
      </Box>
      <GlossaryTermDescription
        description={getTextWithVariables({
          show,
          customFields,
          // Field values only: `stand` is left out, so stand variables — which
          // a manual never offers — stay unresolved.
          ...(standToCustomFields && {task: {standToCustomFields}}),
          description: term.description,
          localizedOrganiser: organiser,
          t,
          locale,
        })}
      />
    </div>
  );
};

export default React.memo(GlossaryTerm, (prev, next) => {
  // Default shallow compare fails when the parent rebuilds the term object
  // (e.g. via filter/map) on every render. Compare by content so the
  // dangerouslySetInnerHTML downstream doesn't get reapplied.
  return (
    prev.term.id === next.term.id &&
    prev.term.title === next.term.title &&
    prev.term.description === next.term.description &&
    isLinkedTerm(prev.term) === isLinkedTerm(next.term) &&
    prev.actions === next.actions &&
    prev.isPrintView === next.isPrintView
  );
});
